import { Checkbox, Flex, Icon, Link, Text } from '@servicetitan/anvil2';
import InfoIcon from '@servicetitan/anvil2/assets/icons/material/round/info.svg';
import { ApplicationDataStore } from '../application/ApplicationDataStore';
import { DisclosureDataStore } from './DisclosureStore';
import {
    useProviderConfig,
    useSecondLookIntegration,
    useSecondLookProviderName,
} from '@servicetitan/financing-workflow-shared';
import { useState, useEffect } from 'react';
import * as styles from './PrequalsGreenSkyDisclosure.module.scss';

export const PrequalsGreenSkyDisclosure = () => {
    const { setAgreedToDisclosures } = DisclosureDataStore.useStore();
    const { applicationData } = ApplicationDataStore.useStore();
    const { continueToHardAppAfterSoft } = useProviderConfig();
    const secondLookIntegration = useSecondLookIntegration();
    const secondLookProviderName = useSecondLookProviderName();
    const { coApplicationTermsUrl } = useProviderConfig(
        secondLookIntegration.secondLookProvider ?? undefined,
    );
    const [applicantAgreed, setApplicantAgreed] = useState(false);
    const [coApplicantAgreed, setCoApplicantAgreed] = useState(false);

    const hasCoApplicant = applicationData?.coApplicant?.hasCoApplicant ?? false;
    const showTurnsDisclosure = continueToHardAppAfterSoft && secondLookIntegration.isEnabled;

    useEffect(() => {
        const allAgreed = hasCoApplicant ? applicantAgreed && coApplicantAgreed : applicantAgreed;
        setAgreedToDisclosures(allAgreed);
    }, [applicantAgreed, coApplicantAgreed, hasCoApplicant, setAgreedToDisclosures]);

    const getTerms = () => {
        return (
            <Flex direction="column" gap="3" className={styles.bulletPoints}>
                <Text variant="body">
                    • You have read, understand, and accept GreenSky® Program's{' '}
                    <Link
                        target="_blank"
                        rel="noreferrer"
                        href="https://www.greensky.com/terms/website_terms_of_use/"
                    >
                        Terms of Use
                    </Link>
                    , and GreenSky® Program's{' '}
                    <Link
                        target="_blank"
                        rel="noreferrer"
                        href="https://www.greensky.com/privacy-policy"
                    >
                        Privacy Notice
                    </Link>{' '}
                    and GreenSky® Program's{' '}
                    <Link
                        target="_blank"
                        rel="noreferrer"
                        href="https://www.greensky.com/information"
                    >
                        Consent to Electronic Records and Communication
                    </Link>
                </Text>
                <Text variant="body">
                    • You are providing written instructions' to the GreenSky® Program under the
                    Fair Credit Reporting Act authorizing the GreenSky® Program to obtain
                    information from your personal credit profile or other information from
                    Experian. You authorize the GreenSky® Program to obtain such information solely
                    to conduct a pre-qualification for credit. You expressly authorize the GreenSky®
                    Program to share with your Merchant and any manufacturer, including,
                    respectively for each, any service providers, parent/affiliate entities, and
                    franchisors/franchisees, the Prequalification decision, including the
                    prequalified credit limit, loan terms, and prequalified APR.
                </Text>
                <Text variant="body">
                    • When you check your rate and review loan offers, a soft inquiry will be
                    performed on your credit report, which won't impact your credit score. Soft
                    inquiries can only be seen by you. When you accept a loan offer and submit your
                    application for review, a hard inquiry will be performed, which may impact your
                    credit score and can be viewed by third parties.
                </Text>
                {showTurnsDisclosure && (
                    <Text variant="body">
                        • I also understand that if GreenSky® Program has no available offers, the
                        above information will be sent to {secondLookProviderName} Financing, and{' '}
                        {secondLookProviderName} Financing may conduct a soft pull of my credit that
                        will not affect my credit score.{' '}
                        {coApplicationTermsUrl && (
                            <Link target="_blank" rel="noreferrer" href={coApplicationTermsUrl}>
                                View terms
                            </Link>
                        )}
                    </Text>
                )}
            </Flex>
        );
    };

    return (
        <Flex direction="column" gap="4">
            <Flex gap="3" alignItems="center" className={styles.infoBanner}>
                <Icon svg={InfoIcon} color="#0265DC" />
                <Text variant="body" className={styles.infoBannerText}>
                    <b>Prequalify with no impact to your credit score!</b>
                </Text>
            </Flex>
            <Checkbox
                label="You, the applicant, agree and understand the following notice:"
                onChange={(e, state) => setApplicantAgreed(state?.checked ?? false)}
            />
            {getTerms()}
            {hasCoApplicant && (
                <>
                    <Checkbox
                        label="You, the co-applicant, agree and understand the following notice:"
                        onChange={(e, state) => setCoApplicantAgreed(state?.checked ?? false)}
                    />
                    {getTerms()}
                </>
            )}
        </Flex>
    );
};
